import React, { useEffect, useState } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import BookingForm from './BookingForm';
import groupPackages1 from '../group-tours/data/groupPackages1';
import * as groupToursService from '@/services/groupTours';
import { useCart } from '../../hooks/useCart';
import { getGroupTourImageUrl } from '@/environments/environment';

const ToursViewPage: React.FC = () => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const id = params.get('id') || '';

    const samplePkg = groupPackages1.find(p => p.id === id);
    const [pkg, setPkg] = useState<any | undefined>(samplePkg);
    // If not found in sample data, try fetching from backend by id
    useEffect(() => {
        let mounted = true;
        const load = async () => {
            if (samplePkg) return; // already have sample
            if (!id) return;
            try {
                const fetched = await groupToursService.getGroupTour(id);
                // normalize id field
                if (fetched) {
                    fetched.id = fetched.id || fetched._id;
                }
                if (mounted) setPkg(fetched || undefined);
            } catch (e) {
                if (mounted) setPkg(undefined);
            }
        };
        load();
        return () => { mounted = false; };
    }, [id, samplePkg]);
    const { cartItems, addToCart, removeFromCart, updateQuantity } = useCart();

    // If package is found and not present in cart, add it so left column mirrors cart behaviour
    useEffect(() => {
        if (!pkg) return;
        const id = (pkg as any).id || (pkg as any)._id;
        const existing = cartItems.find(ci => ci.id === id);
        if (!existing) {
            const image = getGroupTourImageUrl(pkg.imageUrl) || '';
            addToCart({ id, title: pkg.tourTitle, price: pkg.price?.total || 0, image, category: pkg.category, description: pkg.description, itinerary: pkg.itinerary });
        }
    }, [pkg, cartItems, addToCart]); const cartItem = pkg ? cartItems.find(ci => ci.id === pkg.id) : undefined;
    const [localOpenDay, setLocalOpenDay] = useState<number | null>(null);

    return (
        <div className="min-h-screen bg-gray-50">
            <Header />
            <main>
                <section className="py-16">
                    <div className="max-w-6xl mx-auto px-6">
                        {pkg ? (
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                <div className="lg:col-span-2 space-y-6">
                                    {/* Cart-style item display (mirrors CartSection left column) */}
                                    {cartItem && (
                                        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                                            <div>
                                                <div className="w-full overflow-hidden rounded-t-xl relative" style={{ paddingTop: '56.25%' }}>
                                                    <img src={cartItem.image} alt={cartItem.title} className="absolute inset-0 w-full h-full object-cover" />
                                                </div>

                                                <div className="p-6">
                                                    <h2 className="text-2xl font-bold text-gray-900 mb-2">{cartItem.title}</h2>
                                                    <p className="text-sm text-gray-600 mb-4">{cartItem.category}</p>

                                                    <p className="text-gray-700 mb-4 line-clamp-3">{cartItem.description || 'A wonderful travel package tailored for you.'}</p>

                                                    {/* Itinerary Accordion (same behavior as CartSection) */}
                                                    <div className="mt-4">
                                                        <h3 className="text-lg font-semibold text-gray-800 mb-2">Itinerary</h3>
                                                        <div className="space-y-2">
                                                            {cartItem.itinerary?.map((d) => (
                                                                <div key={d.day} className="border rounded-lg overflow-hidden">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setLocalOpenDay(localOpenDay === d.day ? null : d.day)}
                                                                        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100"
                                                                    >
                                                                        <div className="flex items-center gap-3">
                                                                            <div className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center font-semibold">{d.day}</div>
                                                                            <div className="text-sm font-medium text-gray-800">{d.title}</div>
                                                                        </div>
                                                                        <div className="text-gray-500">{localOpenDay === d.day ? <i className="ri-arrow-up-s-line"></i> : <i className="ri-arrow-down-s-line"></i>}</div>
                                                                    </button>

                                                                    {localOpenDay === d.day && (
                                                                        <div className="px-4 py-3 bg-white text-gray-700 text-sm">
                                                                            {d.description}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {/* Price and total */}
                                                    <div className="mt-6">
                                                        <div className="space-y-1">
                                                            <div className="flex items-center justify-between text-sm text-gray-700">
                                                                <span className="font-medium">Price:</span>
                                                                <span>₹{cartItem.price.toLocaleString()}</span>
                                                            </div>
                                                            <div className="flex items-center justify-between text-lg font-semibold text-emerald-600">
                                                                <span className="font-medium">Total:</span>
                                                                <span>₹{(cartItem.price * cartItem.quantity).toLocaleString()}</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Quantity + Remove */}
                                                    <div className="mt-4 flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            <div className="text-sm text-gray-600">Quantity</div>
                                                            <div className="flex items-center gap-2">
                                                                <button
                                                                    onClick={() => updateQuantity(cartItem.id, Math.max(1, cartItem.quantity - 1))}
                                                                    className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg"
                                                                    aria-label="Decrease quantity"
                                                                >
                                                                    <i className="ri-subtract-line"></i>
                                                                </button>
                                                                <div className="w-12 text-center font-medium">{cartItem.quantity}</div>
                                                                <button
                                                                    onClick={() => updateQuantity(cartItem.id, cartItem.quantity + 1)}
                                                                    className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg"
                                                                    aria-label="Increase quantity"
                                                                >
                                                                    <i className="ri-add-line"></i>
                                                                </button>
                                                            </div>
                                                        </div>

                                                        <button
                                                            onClick={() => removeFromCart(cartItem.id)}
                                                            className="text-red-500 hover:text-red-700 p-2"
                                                            aria-label={`Remove ${cartItem.title}`}
                                                        >
                                                            <i className="ri-delete-bin-line text-xl"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex justify-between">
                                        <button
                                            onClick={() => window.history.back()}
                                            className="text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-2"
                                        >
                                            <i className="ri-arrow-left-line"></i>
                                            Continue Browsing
                                        </button>
                                    </div>
                                </div>

                                <div className="lg:col-span-1">
                                    <BookingForm packageId={pkg.id} packageTitle={pkg.tourTitle} price={pkg.price.total} />
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-16">
                                <h2 className="text-2xl font-bold">Package not found</h2>
                                <p className="text-gray-600 mt-2">Please check the link or return to the packages list.</p>
                            </div>
                        )}
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default ToursViewPage;
